import axios from "axios";

const LOCAL_BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${LOCAL_BASE}/api`;

// Real upstream API shared with Super Admin and Auto Shop mobile app.
export const REAL_API = "https://autoshopllm.autoshoppilot.live/v1/";

export const TOKEN_KEY = "ap_token";
export const SHOP_ID_KEY = "ap_shop_id";
export const USER_KEY = "ap_user";
export const REMEMBER_KEY = "ap_remember";

// ---------------------------------------------------------------------------
// Token storage
// ---------------------------------------------------------------------------
const getRemember = () => localStorage.getItem(REMEMBER_KEY) === "1";

export function getToken() {
    return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY) || "";
}

export function getShopId() {
    return localStorage.getItem(SHOP_ID_KEY) || sessionStorage.getItem(SHOP_ID_KEY) || "";
}

export function getUser() {
    const raw = localStorage.getItem(USER_KEY) || sessionStorage.getItem(USER_KEY);
    try {
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

export function setSession({ token, shopId, user, remember }) {
    const store = remember ? localStorage : sessionStorage;
    store.setItem(TOKEN_KEY, token);
    if (shopId) store.setItem(SHOP_ID_KEY, shopId);
    if (user) store.setItem(USER_KEY, JSON.stringify(user));
    if (remember) localStorage.setItem(REMEMBER_KEY, "1");
    else localStorage.removeItem(REMEMBER_KEY);
}

export function clearSession() {
    [localStorage, sessionStorage].forEach((s) => {
        s.removeItem(TOKEN_KEY);
        s.removeItem(SHOP_ID_KEY);
        s.removeItem(USER_KEY);
    });
    localStorage.removeItem(REMEMBER_KEY);
}

// Back-compat alias used by older code paths.
export function setToken(token, remember) {
    setSession({ token, remember });
}
export const clearToken = clearSession;

// ---------------------------------------------------------------------------
// JWT decode (client side) – used to discover the shop MongoDB id when the
// login response does not surface it directly.
// ---------------------------------------------------------------------------
export function decodeJwt(token) {
    try {
        const payload = token.split(".")[1];
        const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
        return JSON.parse(decodeURIComponent(escape(json)));
    } catch {
        return {};
    }
}

function extractShopId(user, token) {
    if (user) {
        const candidates = [
            user.shopMongoId,
            user.shopId,
            user.shop?._id,
            user.shop?.shopMongoId,
        ];
        const hit = candidates.find((c) => typeof c === "string" && c.length >= 12);
        if (hit) return hit;
    }
    const decoded = decodeJwt(token);
    return (
        decoded.shopMongoId ||
        decoded.shopId ||
        decoded.shop?._id ||
        decoded.shop ||
        decoded.sub ||
        ""
    );
}

// ---------------------------------------------------------------------------
// Real API client (shared backend)
// ---------------------------------------------------------------------------
const realClient = axios.create({
    baseURL: REAL_API,
    validateStatus: () => true, // this API uses mixed status codes
});

realClient.interceptors.request.use((config) => {
    const t = getToken();
    if (t) config.headers.Authorization = `Bearer ${t}`;
    return config;
});

// Local backend client (used for not-yet-integrated features)
const localClient = axios.create({ baseURL: API });
localClient.interceptors.request.use((config) => {
    const t = getToken();
    if (t) config.headers.Authorization = `Bearer ${t}`;
    return config;
});

// ---------------------------------------------------------------------------
// Shape adapters – map the real API responses to the UI shape already used
// in ShopDetails / Payments / Subscription pages.
// ---------------------------------------------------------------------------
function mapShopDetails(data) {
    const shop = data?.shop || {};
    const relations = data?.relations || {};
    const allUsers = relations.shopUsers?.allUsers || [];
    // Prefer the explicit adminUser, else first shopAdmin, else first shopManager
    const admin =
        relations.shopUsers?.adminUser ||
        allUsers.find((u) => u.role === "shopAdmin") ||
        allUsers.find((u) => u.role === "shopManager") ||
        null;
    const users = allUsers.map((u) => ({
        id: u._id,
        name: [u.firstname, u.lastname].filter(Boolean).join(" ").trim() || u.email,
        email: u.email,
        role:
            u.role === "shopAdmin"
                ? "Owner"
                : u.role === "shopManager"
                    ? "Manager"
                    : u.role || "Staff",
    }));
    const agent = shop.shopAgentDetails || {};
    const wallet = shop.wallet || {};
    const subs = shop.subscriptions || [];
    const activeSub =
        subs.find((s) => s.status === "ACTIVE") ||
        subs.find((s) => s.status === "CANCELLED") ||
        subs[0] ||
        null;

    return {
        id: shop._id,
        shopId: shop.shopId,
        basic: {
            name: shop.shopName || "",
            business_name: shop.legalBusinessName || "",
            address: shop.address || "",
            city: shop.city || "",
            state: shop.state || "",
            zip: shop.zipCode || "",
            phone: shop.shopPhoneNumber || "",
            email: shop.shopEmail || "",
            website: shop.websiteLink || "",
            timezone: shop.shopTimezone || "",
            bays: shop.noOfBays || 0,
        },
        admin: admin
            ? {
                  name:
                      [admin.firstname, admin.lastname].filter(Boolean).join(" ").trim() ||
                      admin.email,
                  email: admin.email || "",
                  phone: shop.contactNumber || "",
                  role: admin.role === "shopAdmin" ? "Owner" : admin.role || "Owner",
              }
            : { name: shop.contactPerson || "", email: "", phone: shop.contactNumber || "", role: "Owner" },
        agent: {
            agent_name: agent.agentName || "",
            agent_phone: agent.phoneAgentNumber || "",
            voice: shop.shopVoice || "",
            greeting: shop.whyChooseUsSection?.description || "",
            business_hours: formatOpeningHours(shop.openingHours),
            languages: ["English"],
            forwarding_number: agent.handoverNumber || "",
            sms_number: agent.smsAgentNumber || "",
            website_url: agent.websiteUrl || agent.agentLink || "",
        },
        users,
        subscription: activeSub
            ? {
                  plan_id: (activeSub.packCode || "").toLowerCase(),
                  plan_name: activeSub.packName || activeSub.title || activeSub.packCode,
                  status:
                      activeSub.status === "ACTIVE"
                          ? "active"
                          : activeSub.status === "CANCELLED"
                              ? "cancelled"
                              : "none",
                  cycle_start: activeSub.startDate,
                  cycle_end: activeSub.nextRenewalDate,
                  cancels_at_cycle_end: !!activeSub.cancelledAt,
                  amount: activeSub.amount,
                  currency: activeSub.currency,
              }
            : { status: "none" },
        credits: {
            subscription_credits: wallet.creditBalance || 0,
            recharge_credits: wallet.walletBalance || 0,
            auto_recharge_threshold: wallet.autoWalletRechargeThresholdAmount || 100,
            auto_recharge_enabled: !!wallet.autoWalletRechargeEnabled,
            currency: wallet.walletCurrency || "USD",
            total_balance: wallet.totalBalance || 0,
        },
        payments: [], // payments come from /shop/wallet/transactions
        raw: data,
    };
}

function formatOpeningHours(hrs) {
    if (!hrs) return "";
    const order = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    const parts = [];
    order.forEach((day) => {
        const arr = hrs[day] || [];
        const s = arr[0];
        if (s?.status === "open" && s.openTime && s.closeTime) {
            parts.push(`${day.slice(0, 3)} ${s.openTime}-${s.closeTime}`);
        }
    });
    return parts.join(" · ");
}

function mapTransaction(t) {
    const isCredit = t.label === "CREDIT";
    const method = t.paypalOrderId ? "PayPal" : t.transactionStatus || "-";
    const statusMap = {
        success: "paid",
        pending: "pending",
        failed: "failed",
        refunded: "refunded",
    };
    const typeMap = {
        WALLET_RECHARGE: "recharge",
        SUBSCRIPTION: "subscription",
    };
    const eventTypeMap = {
        SUBSCRIPTION_PAYMENT: "subscription",
        SUBSCRIPTION_RENEWAL: "subscription",
        SUBSCRIPTION_CANCELLED: "subscription",
        WALLET_RECHARGE: "recharge",
        AUTO_WALLET_RECHARGE: "recharge",
        WALLET_USAGE_DEDUCTION: "usage",
    };
    return {
        id: t._id,
        date: t.createdAt,
        description: t.title || t.packName || t.eventType || "Transaction",
        type: typeMap[t.transactionType] || eventTypeMap[t.eventType] || "one_time",
        amount: Number(t.amount) || 0,
        status: statusMap[t.transactionStatus] || (t.transactionStatus ? "pending" : "paid"),
        method,
        reference: t.transactionId || t._id,
        direction: isCredit ? "credit" : "debit",
        tags: t.tags || [],
        balance_after: t.walletBalanceAfter,
        has_receipt: !!t._id,
        raw: t,
    };
}

// ---------------------------------------------------------------------------
// Login – calls real /auth/login, handles mixed status per the integration doc
// ---------------------------------------------------------------------------
async function login({ email, password, remember }) {
    const { status, data } = await realClient.post("/auth/login", {
        email,
        password,
        deviceType: "web",
    });

    if (status === 200 && data?.success === true && data?.data?.token) {
        const token = data.data.token;
        const user = data.data.user;
        const shopId = extractShopId(user, token);
        setSession({ token, shopId, user, remember });
        return {
            kind: "success",
            token,
            user,
            shopId,
            name:
                [user?.firstname, user?.lastname].filter(Boolean).join(" ").trim() ||
                user?.email ||
                "User",
        };
    }
    if (status === 203) {
        const msg = data?.message || "Account state issue";
        const isUnverified = /verif/i.test(msg);
        return { kind: isUnverified ? "unverified" : "inactive", message: msg };
    }
    if (status === 400) {
        return { kind: "validation_error", message: data?.message || "Invalid request" };
    }
    if (status === 200 && data?.success === false) {
        const msg = data?.message || "Login failed";
        // Friendlier message for the upstream's misleading wording.
        const friendly = /email does not exist/i.test(msg)
            ? "Invalid email or password"
            : msg;
        return { kind: "auth_error", message: friendly };
    }
    return { kind: "server_error", message: data?.message || "Unexpected error" };
}

// ---------------------------------------------------------------------------
// Shop details – real API
// ---------------------------------------------------------------------------
async function me() {
    const shopId = getShopId();
    if (!shopId) throw new Error("Missing shop id");
    const { status, data } = await realClient.get(`/shop/shop-details/${shopId}`);
    if (status !== 200 || !data?.success) {
        const msg = data?.message || `Failed (${status})`;
        const err = new Error(msg);
        err.response = { status, data };
        throw err;
    }
    return mapShopDetails(data.data);
}

// ---------------------------------------------------------------------------
// Wallet transactions (payment history) – real API
// ---------------------------------------------------------------------------
async function payments({ startDate, endDate, direction, type, page = 1, limit = 100 } = {}) {
    const params = { page, limit };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (direction) params.direction = direction;
    if (type) params.type = type;
    const { status, data } = await realClient.get("/shop/wallet/transactions", { params });
    if (status !== 200 || !data?.success) {
        const err = new Error(data?.message || `Failed (${status})`);
        err.response = { status, data };
        throw err;
    }
    const txs = (data.data?.transactions || []).map(mapTransaction);
    return {
        payments: txs,
        summary: data.data?.summary || null,
        pagination: data.data?.pagination || null,
    };
}

// ---------------------------------------------------------------------------
// Subscription list – real API (returns list of shops; we filter to current)
// ---------------------------------------------------------------------------
async function subscriptionList() {
    const { status, data } = await realClient.get("/shop/subscription-list", {
        params: { limit: 100 },
    });
    if (status !== 200 || !data?.success) {
        const err = new Error(data?.message || `Failed (${status})`);
        err.response = { status, data };
        throw err;
    }
    return data.data || { shops: [] };
}

// ---------------------------------------------------------------------------
// Master wallet packs (subscription + recharge + one-time) – real API
// ---------------------------------------------------------------------------
async function getMasterPacks(category) {
    const params = {};
    if (category) params.category = category;
    const { status, data } = await realClient.post(
        "/master/wallet-packs/seed",
        {},
        { params },
    );
    if (status !== 200 || !data?.success) {
        const err = new Error(data?.message || `Failed (${status})`);
        err.response = { status, data };
        throw err;
    }
    const grouped = data.data?.results || {};
    const flatten = (arr) => (arr || []).map((x) => x.doc);
    return {
        recharge: flatten(grouped.RECHARGE),
        subscription: flatten(grouped.SUBSCRIPTION),
        onetime: flatten(grouped.ONETIMEPAYMENT),
        category: data.data?.category,
        updated: data.data?.updated,
    };
}

// Subscription pack -> UI plan adapter.
function adaptSubscriptionPack(doc) {
    const code = doc.packCode || "";
    const iconByCode = { STARTER: "rocket", GROWTH: "trending-up", PRO: "gem" };
    const colorByCode = {
        STARTER: "#3b82f6",
        GROWTH: "#a855f7",
        PRO: "#f59e0b",
    };
    const description =
        doc.numberOfBays <= 1
            ? "Ideal for getting started at a smaller scale"
            : doc.numberOfBays <= 4
                ? `Ideal for ${doc.numberOfBays} bays`
                : `Ideal for shops with ${doc.numberOfBays} or more bays`;
    return {
        id: code.toLowerCase(),
        packCode: code,
        name: doc.title || doc.packName || code,
        price: doc.amount,
        currency: doc.currency || "USD",
        period:
            doc.type === "MONTHLY" || doc.durationInDays === 30
                ? "month"
                : doc.durationInDays
                    ? `${doc.durationInDays} days`
                    : "month",
        description,
        credits_included: doc.credits || 0,
        features: doc.benefits && doc.benefits.length ? doc.benefits : [],
        excluded_features: doc.isUpselling ? [] : ["Upselling"],
        icon: iconByCode[code] || "rocket",
        icon_color: colorByCode[code] || "#3b82f6",
        tier: 0, // computed by caller from price ordering
        raw: doc,
    };
}

// Recharge pack -> UI credit-pack adapter.
function adaptRechargePack(doc) {
    const credits = (doc.amount || 0) * (doc.conversionRate || 0);
    return {
        packCode: doc.packCode,
        amount: doc.amount,
        currency: doc.currency || "USD",
        credits: Math.round(credits),
        conversionRate: doc.conversionRate,
        title: doc.title,
        raw: doc,
    };
}
async function getOneTimePacks() {
    const { status, data } = await realClient.get("/master/wallet-packs", {
        params: { category: "ONETIMEPAYMENT" },
    });
    if (status !== 200 || !data?.success) {
        const err = new Error(data?.message || `Failed (${status})`);
        err.response = { status, data };
        throw err;
    }
    return data.data?.packs || [];
}

// ---------------------------------------------------------------------------
// Receipt PDF – real API
// ---------------------------------------------------------------------------
async function downloadReceipt(transactionId) {
    const response = await axios.get(`${REAL_API}/shop/wallet/generate-receipt`, {
        params: { transaction_id: transactionId },
        responseType: "blob",
        validateStatus: () => true,
        headers: {
            Authorization: `Bearer ${getToken()}`,
        },
    });
    const contentType = response.headers?.["content-type"] || "";
    if (response.status !== 200 || !contentType.includes("application/pdf")) {
        let msg = "Failed to generate receipt";
        try {
            const text = await response.data.text?.();
            if (text) msg = JSON.parse(text).message || msg;
        } catch {
            /* ignore */
        }
        const err = new Error(msg);
        err.status = response.status;
        throw err;
    }
    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `receipt-${transactionId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Local mock (features without a real API yet)
// ---------------------------------------------------------------------------
export const api = {
    // Real APIs
    login,
    me,
    payments,
    subscriptionList,
    getOneTimePacks,
    getMasterPacks,
    adaptSubscriptionPack,
    adaptRechargePack,
    downloadReceipt,
    updateShopSection: async (shopId, action, payload) => {
        const { status, data } = await realClient.patch("/shop/profile/section", {
            action,
            shopId,
            payload,
        });
        if (status !== 200 || !data?.success) {
            const err = new Error(data?.message || `Failed (${status})`);
            err.response = { status, data };
            throw err;
        }
        return data.data;
    },
    createPayPalOrder: async (productId, productType) => {
        const { status, data } = await realClient.post("/paypal/create-order", {
            productId,
            productType,
        });
        if (status !== 200 || !data?.success) {
            const err = new Error(data?.message || `Failed to create order`);
            err.response = { status, data };
            throw err;
        }
        return data.data; // { orderId, orderNo, paypalOrderId, checkoutUrl, paymentUrl, amount, currency, productType }
    },

    // Local mock (to be replaced when the user supplies the corresponding APIs)
    plans: () => localClient.get("/subscription-plans").then((r) => r.data),
    updateBasic: (p) => localClient.put("/shop/basic", p).then((r) => r.data),
    updateAdmin: (p) => localClient.put("/shop/admin", p).then((r) => r.data),
    updateAgent: (p) => localClient.put("/shop/agent", p).then((r) => r.data),
    createUser: (p) => localClient.post("/shop/users", p).then((r) => r.data),
    changePassword: (id, new_password) =>
        localClient.put(`/shop/users/${id}/password`, { new_password }).then((r) => r.data),
    deleteUser: (id) => localClient.delete(`/shop/users/${id}`).then((r) => r.data),
    subscribe: (plan_id) => localClient.post("/shop/subscribe", { plan_id }).then((r) => r.data),
    cancel: () => localClient.post("/shop/cancel-subscription").then((r) => r.data),
    recharge: (amount, credits) =>
        localClient.post("/shop/recharge", { amount, credits }).then((r) => r.data),
    setAutoRecharge: (threshold, amount) =>
        localClient.put("/shop/auto-recharge", { threshold, amount }).then((r) => r.data),
    oneTime: (item) => localClient.post("/shop/one-time-payment", { item }).then((r) => r.data),
    paypalCreate: (purpose, amount, meta) =>
        localClient.post("/paypal/create-order", { purpose, amount, meta }).then((r) => r.data),
    paypalCapture: (order_id) =>
        localClient.post("/paypal/capture-order", { order_id }).then((r) => r.data),
};
